/**
 * URL Validate Tool
 *
 * Validate URLs using the URL constructor and show parsed components.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const UrlValidateTool: ITransformerTool = {
  id: 'url-validate',
  name: 'URL Validate',
  description: 'Validate URLs and show parsed components',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['url', 'validate', 'check', 'verify', 'parse', 'link'],

  seo: {
    slug: 'url-validator',
    pageTitle: 'URL Validator - Free Online URL Checker',
    metaDescription: 'Validate URLs and see parsed components like protocol, host, port, path, and query parameters. Fast, private, runs entirely in your browser.',
    keywords: ['url validator', 'url checker', 'validate url online', 'url parser', 'link checker'],
    relatedTools: ['url-parser', 'email-validate', 'extract-urls'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.URL],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const lines = input.trim().split('\n').map(l => l.trim()).filter(Boolean);
    const results: string[] = [];

    for (const line of lines) {
      try {
        const url = new URL(line);
        const parts: string[] = [
          `✓ ${line}`,
          `  Protocol: ${url.protocol}`,
          `  Host: ${url.hostname}`,
        ];

        if (url.port) parts.push(`  Port: ${url.port}`);
        if (url.pathname && url.pathname !== '/') parts.push(`  Path: ${url.pathname}`);
        if (url.search) parts.push(`  Query: ${url.search}`);
        if (url.hash) parts.push(`  Hash: ${url.hash}`);
        if (url.username) parts.push(`  Username: ${url.username}`);
        if (url.password) parts.push(`  Password: ${url.password}`);

        const params = url.searchParams;
        if ([...params].length > 0) {
          parts.push('  Parameters:');
          for (const [key, value] of params) {
            parts.push(`    ${key} = ${value}`);
          }
        }

        parts.push(`  Origin: ${url.origin}`);
        results.push(parts.join('\n'));
      } catch {
        results.push(`✗ ${line}\n  Error: Invalid URL — could not be parsed`);
      }
    }

    return results.join('\n\n');
  },
};
